'use client';

import { Box, Flex, HStack, IconButton, Separator, Text } from '@chakra-ui/react';
import ChatInput from '@components/common/ChatInput';
import { useOrgSlug } from 'hooks/useOrgSlug';
import { memo, useCallback, useState } from 'react';
import { LuSparkles, LuX } from 'react-icons/lu';

const PANEL_WIDTH = '450px';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const ChatMessageBubble = memo(({ role, content }: ChatMessage) => (
  <Flex
    alignSelf={role === 'user' ? 'flex-end' : 'flex-start'}
    bg={role === 'user' ? 'surface.inverse' : 'surface.background'}
    color={role === 'user' ? 'text.inverse' : 'text.primary'}
    px={3}
    py={2}
    borderRadius="md"
    maxW="85%"
  >
    <Text fontSize="xs">{content}</Text>
  </Flex>
));
ChatMessageBubble.displayName = 'ChatMessageBubble';

const ChatEmptyState = memo(() => (
  <Flex h="full" alignItems="center" justifyContent="center" p={4}>
    <Text color="text.placeholder" fontSize="xs" textAlign="center">
      Start a conversation by asking anything...
    </Text>
  </Flex>
));
ChatEmptyState.displayName = 'ChatEmptyState';

const ChatToolbar = memo(({ onClose }: { onClose: () => void }) => (
  <Flex alignItems="center" px={3} py={2} gap={2} flexShrink={0}>
    <Text fontSize="sm" fontWeight="semibold" color="text.primary">
      AI Chat
    </Text>
    <HStack ml="auto" gap={1}>
      <IconButton
        aria-label="Close chat"
        variant="ghost"
        size="xs"
        color="text.secondary"
        onClick={onClose}
      >
        <LuX />
      </IconButton>
    </HStack>
  </Flex>
));
ChatToolbar.displayName = 'ChatToolbar';

interface AIChatProps {
  topOffset?: string;
}

const AIChat = ({ topOffset = '16px' }: AIChatProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const orgSlug = useOrgSlug();

  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleSubmit = useCallback(() => {
    const trimmed = prompt.trim();
    if (!trimmed) return;
    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setPrompt('');
  }, [prompt]);

  if (orgSlug == null) {
    return null;
  }

  if (!isOpen) {
    return (
      <IconButton
        aria-label="Open AI chat"
        onClick={handleOpen}
        position="fixed"
        bottom={6}
        right={6}
        zIndex="popover"
        size="lg"
        borderRadius="full"
        bg="surface.inverse"
        color="text.inverse"
        _hover={{ opacity: 0.9 }}
        boxShadow="md"
      >
        <LuSparkles />
      </IconButton>
    );
  }

  return (
    <Flex
      flexDirection="column"
      w={{ base: 'auto', md: PANEL_WIDTH }}
      position="fixed"
      top={topOffset}
      bottom={4}
      right={4}
      left={{ base: 4, md: 'auto' }}
      zIndex="popover"
      bg="surface.background"
      borderWidth="1px"
      borderColor="border.light"
      borderRadius="md"
      boxShadow="lg"
    >
      <ChatToolbar onClose={handleClose} />
      <Separator borderColor="border.light" />
      <Box flex={1} minH={0} bg="surface.hover" mx={3} mt={3} overflowY="auto" borderRadius="sm">
        {messages.length === 0 ? (
          <ChatEmptyState />
        ) : (
          <Flex flexDirection="column" gap={3} p={3}>
            {messages.map((msg, i) => (
              <ChatMessageBubble key={i} role={msg.role} content={msg.content} />
            ))}
          </Flex>
        )}
      </Box>
      <Box px={3} pt={3} pb={2} flexShrink={0}>
        <ChatInput
          value={prompt}
          onChange={setPrompt}
          onSubmit={handleSubmit}
          placeholder="Ask anything..."
        />
      </Box>
    </Flex>
  );
};

export default memo(AIChat);
